import { useState, type FormEvent } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ApiError, confirmAdminPasswordReset, useBrandName } from "@/lib/api";
import { ShieldIcon } from "@/components/Icons";

/**
 * Phase 35 Slice D — super-admin set-password / reset page, mounted at
 * `/admin/password-reset/:token` (the link emailed on invite + forgot-password).
 * Reads the token from the route, asks for a new password, and POSTs to the
 * anonymous `/admin-api/auth/password-reset/{token}` endpoint. On success the
 * super-admin signs in at `/admin/login` with the new password.
 */
export default function AdminPasswordResetPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const brandName = useBrandName();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    if (password.length < 12) {
      setError(t("adminPasswordReset.errorTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("adminPasswordReset.errorMismatch"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await confirmAdminPasswordReset(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(t("adminPasswordReset.errorInvalidToken"));
      } else {
        console.error("admin password reset failed", err);
        setError(t("adminPasswordReset.errorGeneric"));
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6 rounded-lg border border-border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-md bg-accent/15 ring-1 ring-accent/25 text-accent">
            <ShieldIcon size={18} />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t("adminPasswordReset.title")}
            </p>
            <h1 className="text-lg font-semibold tracking-tight">
              {brandName ? `${brandName} · admin` : "Admin"}
            </h1>
          </div>
        </div>

        {done ? (
          <>
            <p className="rounded-md border border-border bg-muted/20 px-3 py-2 text-sm">
              {t("adminPasswordReset.successTitle")}
            </p>
            <Link
              to="/login"
              className="block w-full rounded-md bg-accent px-4 py-2 text-center text-sm font-medium text-accent-foreground"
            >
              {t("adminPasswordReset.backToLogin")}
            </Link>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="text-sm font-medium">{t("adminPasswordReset.newPasswordLabel")}</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">{t("adminPasswordReset.confirmLabel")}</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
            {error && (
              <p
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition disabled:opacity-50"
            >
              {submitting ? t("adminPasswordReset.submitting") : t("adminPasswordReset.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
